import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

export function safeJsonParse(value: string | null | undefined, fallback: any = null): any {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  getPrivelegeEvent() {
    throw new Error('Method not implemented.');
  }
  baseUrl = environment.baseApiUrl;
  endpointUrl = 'preferences/template';
  private static loaderEnabled: boolean = false;
  private refreshClickEvent = new Subject<any>();
  private privelegeClickEvent = new Subject<any>();
  privilegesUpdated$ = new Subject<void>();
  constructor(private http: HttpClient) {
  }
  onClickSendRefreshEvent(route: any) {
    this.refreshClickEvent.next(route);
  }

  getRefreshEvent(): Observable<any> {
    return this.refreshClickEvent.asObservable();
  }
  getColumnSettings(preferenceId: Number) {
    return this.http.get<any>(this.baseUrl + 'preferences/columns-list?preference_type=' + preferenceId);
  }
  getDMSHandbook() {
    return this.http.get<any>(this.baseUrl + 'document/v1/hand-book');
  }
  getNotificationList() {
    return this.http.get<any>(this.baseUrl + 'notification/list');
  }
  getNotificationCount() {
    return this.http.get<any>(this.baseUrl + 'notification/count');
  }
  postNotificationRead(body: any = {}) {
    return this.http.post(this.baseUrl + 'notification/read', body);
  }
  createFilterTemplate(templateBody: {}) {
    return this.http.post(this.baseUrl + this.endpointUrl, templateBody);
  }
  getProfileDetails() {
    return this.http.get<any>(this.baseUrl + 'user/v1/profile');
  }
  updateDatetimeFormat(datetimeFormat: string) {
    return this.http.patch<any>(this.baseUrl + 'user/v1/profile/preferences', { datetime_format: datetimeFormat });
  }
  getUserListJSONDetails() {
    return this.http.get<any>(this.baseUrl + 'user/v1/json-info');
  }
  getAnnouncementList(body: {}) {
    return this.http.post<any>(environment.baseApiUrl + 'notification/v1/announcement/list', body)
  }

  get loaderEnabled() {
    return CommonService.loaderEnabled;
  }

  public static showLoader() {
    setTimeout(() => {
      this.loaderEnabled = true;
    }, 0);
  }

  public static hideLoader() {
    setTimeout(() => {
      this.loaderEnabled = false;
    }, 0);
  }

  /**
   * Format date as YYYYMMDDHHMMSS (e.g., 20260629145030)
   */
  getFormattedExportDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = ('0' + (now.getMonth() + 1)).slice(-2);
    const day = ('0' + now.getDate()).slice(-2);
    const hours = ('0' + now.getHours()).slice(-2);
    const minutes = ('0' + now.getMinutes()).slice(-2);
    const seconds = ('0' + now.getSeconds()).slice(-2);
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }

  /**
   * Generic method to download file with timestamp in filename
   * @param blob - File blob data
   * @param baseFileName - File name without extension (e.g., 'users_list')
   * @param fileExtension - File extension (e.g., 'xlsx', 'csv', 'json')
   */
  private appendFilenameTimestamp(fileName: string, defaultExtension: string): string {
    const timestamp = this.getFormattedExportDate();
    const trimmed = fileName.trim();
    const hasExtension = /\.[^\.]+$/.test(trimmed);

    if (hasExtension) {
      const lastDotIndex = trimmed.lastIndexOf('.');
      return `${trimmed.slice(0, lastDotIndex)}_${timestamp}${trimmed.slice(lastDotIndex)}`;
    }

    return `${trimmed}_${timestamp}.${defaultExtension}`;
  }

  downloadFile(blob: Blob, fileName: string, fileExtension: string = 'xlsx'): void {
    const finalFileName = this.appendFilenameTimestamp(fileName, fileExtension);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  public static convertInputToDesiredFormat(parsedInput: any[]): string | null {
    if (!Array.isArray(parsedInput)) {
      return null;
    }

    const result: { [key: string]: boolean } = {};

    parsedInput.forEach(item => {
      if (item.key && item.value !== undefined) {
        result[item.key] = item.value;
      } else {
      }
    });

    return JSON.stringify(result);
  }

  public static convertToPayload(dropdown_list: any) {
    let input: any;
    try {
      input = JSON.parse(dropdown_list);
    } catch (e) {
      alert("Invalid JSON format. Please enter a valid JSON.");
      return null;
    }
    let data_dict: { [key: string]: any } = {};
    if (Array.isArray(input)) {
      for (let item of input) {
        if (item.name && item.value !== undefined) {
          data_dict[item.name] = item.value;
        } else {
          alert("Array item must contain 'name' and 'value' properties.");
          return null;
        }
      }
    } else if (typeof input === 'object') {
      data_dict = input;
    } else {
      alert("Input must be a JSON object or array.");
      return null;
    }
    return data_dict
  }

  public static enumToArray(enumObject: any, objectBody: any): any[] {
    return Object.keys(enumObject)
      .filter(key => !Number.isNaN(Number(enumObject[key])))
      .map(key => ({
        [objectBody.key1]: enumObject[key],
        [objectBody.key2]: key.replaceAll('_', ' ')
      }));
  }

  public static enumCharToArray(enumObject: any): { id: string, name: string }[] {
    return Object.keys(enumObject).map(key => ({ id: key, name: enumObject[key] }));
  }

  public static generateFileUrl(base64Data: string, fileName: string): string | null {
    const binaryData = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const byteArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }
    let contentType: string;

    const fileType = fileName.split('.').pop()?.toLowerCase();
    switch (fileType) {
      case 'pdf':
        contentType = 'application/pdf';
        break;
      case 'txt':
        contentType = 'text/plain';
        break;
      case 'edi':
        contentType = 'text/plain';
        break;
      case 'json':
        contentType = 'application/json';
        break;
      case 'xls':
        contentType = 'application/vnd.ms-excel';
        break;
      case 'xlsx':
        contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'docx':
        contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        break;
      case 'gif':
        contentType = 'image/gif';
        break;
      case 'jpg':
      case 'jpeg':
        contentType = 'image/jpeg';
        break;
      case 'png':
        contentType = 'image/png';
        break;
      case 'xml': // Added case for XML
        contentType = 'application/xml';
        break;
      default:
        return null;
    }

    const blob = new Blob([arrayBuffer], { type: contentType });
    return window.URL.createObjectURL(blob);
  }

  public static openFileInNewTab(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  public static convertToFormData(formValue: any, fileKey = null): FormData {
    const formData = new FormData();
    for (const key in formValue) {
      if (formValue.hasOwnProperty(key)) {
        if (key === fileKey) {
          formValue[key]?.forEach((file: any) => {
            formData.append(key, file);
          });
        }
        else if (formValue[key] == null) {
          formData.append(key, '');
        }
        else if (typeof formValue[key] === 'object' && !(formValue[key] instanceof File)) {
          formData.append(key, JSON.stringify(formValue[key] ?? ''));
        } else {
          formData.append(key, formValue[key] ?? '');
        }
      }
    }
    return formData;
  }

  public static getPrivilegeList() {
    const userDetails = safeJsonParse(localStorage.getItem('userDetails'), {});
    if (userDetails) {
      return userDetails?.privileges;
    }
  }

  public static getSuperUser() {
    const userDetails = safeJsonParse(localStorage.getItem('userDetails'), {});
    if (userDetails) {
      return userDetails?.is_superuser;
    }
  }

  public static estimateWidthFromWordCount(wordCount: any, avgCharPerWord = 5, pxPerChar = 7, pxPerSpace = 7) {
    if (wordCount === 0) {
      return 100;
    }
    let totalChars = wordCount * avgCharPerWord;
    let charWidth = totalChars * pxPerChar;
    let spaceWidth = (wordCount - 1) * pxPerSpace;
    return charWidth + spaceWidth;
  }

  downloadJson(data: any, filename: string = 'data.json') {
    const jsonStr = JSON.stringify(data, null, 2); // pretty format
    const blob = new Blob([jsonStr], { type: 'application/json' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  }

}
